'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Longest a programmatic smooth scroll is assumed to run; the spy stays quiet meanwhile. */
const SMOOTH_SCROLL_LOCK_MS = 900;

/**
 * Scroll-spy over page sections that live under a sticky header (the food-delivery-menu pattern).
 *
 * `activeId` is the last section whose top has reached the header, or `null` while the reader
 * is above the first one. `scrollTo(id)` scrolls a section just below the header and `scrollTo(null)`
 * back to the top of the page; both pin `activeId` while the smooth scroll runs, so the tab the reader
 * clicked does not flicker through every section it passes on the way.
 *
 * @param ids Element ids of the sections, in document order.
 * @param getOffset Height of the sticky header in px; read lazily so it can be measured from the DOM.
 * @param getBottom Current viewport-relative bottom edge of the header (differs from `getOffset` until the header has stuck).
 */
export default function useScrollSpy(ids: string[], getOffset: () => number, getBottom: () => number = getOffset) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const lockedUntil = useRef(0);

    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            if (Date.now() < lockedUntil.current) {
                return;
            }
            // "All" until the sticky header has actually pinned
            if (getBottom() > getOffset() + 1) {
                setActiveId(null);
                return;
            }
            // The last section whose top has reached the header: its cards are what the reader sees under it
            const bottom = getBottom();
            let current: string | null = ids[0] ?? null;
            for (const id of ids) {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top <= bottom + 32) {
                    current = id;
                } else {
                    break;
                }
            }
            setActiveId(current);
        };
        const schedule = () => {
            frame ||= requestAnimationFrame(update);
        };
        schedule();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
        };
    }, [ids, getBottom, getOffset]);

    const scrollTo = useCallback(
        (id: string | null) => {
            const target = id === null ? null : document.getElementById(id);
            if (id !== null && !target) {
                return;
            }
            const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const top = target ? target.getBoundingClientRect().top + window.scrollY - getOffset() : 0;
            lockedUntil.current = instant ? 0 : Date.now() + SMOOTH_SCROLL_LOCK_MS;
            setActiveId(id);
            window.scrollTo({ top: Math.max(0, top), behavior: instant ? 'auto' : 'smooth' });
        },
        [getOffset]
    );

    return { activeId, scrollTo };
}
